import { useMemo } from "react";
import {
  AlertCircle,
  ArrowLeft,
  BadgeIndianRupee,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
} from "lucide-react";
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  getSamplePdfPath,
  useFormData,
} from "./formData";

export default function FormDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedRoute =
    searchParams.get("route") || "";

  const {
    forms,
    loading,
    error,
    reload,
  } = useFormData();

  /*
   * Find the requested form after forms have
   * been loaded from the database.
   */
  const form = useMemo(
    () =>
      forms.find(
        (item) =>
          item.route === requestedRoute
      ),
    [forms, requestedRoute]
  );

  /*
   * Wait until form data has loaded before
   * deciding whether the form exists.
   */
  if (loading) {
    return (
      <main className="flex min-h-[calc(100vh-65px)] items-center justify-center px-4 py-10">
        <section className="w-full max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-xl">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />

          <h1 className="mt-4 text-xl font-bold">
            Loading form details
          </h1>

          <p className="mt-2 text-sm text-muted-foreground">
            Please wait while the form information
            is being loaded.
          </p>
        </section>
      </main>
    );
  }

  /*
   * Show the API/database error separately.
   */
  if (error) {
    return (
      <main className="flex min-h-[calc(100vh-65px)] items-center justify-center px-4 py-10">
        <section className="w-full max-w-lg rounded-3xl border border-red-300 bg-red-50 p-8 text-center shadow-xl">
          <AlertCircle className="mx-auto h-12 w-12 text-red-600" />

          <h1 className="mt-4 text-2xl font-bold text-red-700">
            Unable to load form details
          </h1>

          <p className="mt-2 text-sm text-red-600">
            {error}
          </p>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={reload}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-red-600 px-6 text-sm font-semibold text-white transition hover:bg-red-700"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/Fill_My_Form")
              }
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-red-300 bg-white px-6 text-sm font-semibold text-red-700 transition hover:bg-red-100"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to forms
            </button>
          </div>
        </section>
      </main>
    );
  }

  /*
   * Show this only after loading has completed
   * and the requested route was not found.
   */
  if (!form) {
    return (
      <main className="flex min-h-[calc(100vh-65px)] items-center justify-center px-4 py-10">
        <section className="w-full max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-xl">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />

          <h1 className="mt-4 text-2xl font-bold">
            Form details unavailable
          </h1>

          <p className="mt-2 text-sm text-muted-foreground">
            The requested form could not be found.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/Fill_My_Form")
            }
            className="btn-gradient mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to forms
          </button>
        </section>
      </main>
    );
  }

  /*
   * sampleroute from the database is used first.
   * getSamplePdfPath provides a fallback when empty.
   */
  const samplePdf =
    getSamplePdfPath(form);

  const viewerUrl = samplePdf
    ? `${samplePdf}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`
    : "";

  return (
    <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
      <div className="pointer-events-none absolute inset-0 flex justify-center">
        <div className="mt-10 h-[520px] w-[520px] rounded-full bg-gradient-to-tr from-sky-400/15 to-violet-500/20 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-foreground/5 hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <section className="overflow-hidden rounded-3xl border border-border/70 bg-background/85 shadow-xl backdrop-blur-xl">
          <header className="border-b border-border/70 p-6 sm:p-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-muted/40">
                <img
                  src={form.image}
                  alt={form.title}
                  className="h-10 max-w-[80%] object-contain"
                />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Form Details
                </p>

                <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
                  {form.title}
                </h1>

                {form.name && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {form.name}
                  </p>
                )}
              </div>
            </div>
          </header>

          <div className="grid gap-5 p-5 sm:p-7 lg:grid-cols-[280px_minmax(0,1fr)]">
            <aside className="space-y-4">
              <DetailItem
                icon={FileText}
                label="Form Name"
                value={form.title}
              />

              <DetailItem
                icon={BadgeIndianRupee}
                label="Form Charges"
                value={`₹${Number(
                  form.charge || 0
                ).toFixed(2)}`}
              />

              <div className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Eye className="h-4 w-4 text-primary" />
                  Sample form
                </div>

                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  This PDF is provided only as a
                  sample preview of the blank form.
                </p>
              </div>
            </aside>

            <section className="overflow-hidden rounded-2xl border border-border/70 bg-muted/20">
              <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Eye className="h-4 w-4 text-primary" />
                  PDF Sample Preview
                </div>

                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold text-primary">
                  View only
                </span>
              </div>

              {viewerUrl ? (
                <iframe
                  src={viewerUrl}
                  title={`${form.title} sample PDF`}
                  className="h-[70vh] min-h-[520px] w-full border-0 bg-white"
                />
              ) : (
                <div className="flex min-h-[520px] items-center justify-center p-6 text-center text-sm text-muted-foreground">
                  Sample PDF is not currently
                  available.
                </div>
              )}
            </section>
          </div>
        </section>
      </div>
    </main>
  );
}

function DetailItem({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        <Icon className="h-4 w-4 text-primary" />
        {label}
      </div>

      <p className="mt-2 break-words text-base font-semibold">
        {value}
      </p>
    </div>
  );
}